import React, { useState, useEffect } from 'react';
import api from '../services/api';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    alpha: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    expired: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };
  const label = status === 'alpha' ? 'Alpha' : status === 'active' ? 'Active' : 'Expired';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${map[status] || map.expired}`}>
      {label}
    </span>
  );
}

function TierBadge({ tier }) {
  const map = {
    premium: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    alpha: 'bg-amber-500 text-white',
    starter: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${map[tier] || map.starter}`}>
      {tier}
    </span>
  );
}

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [subsRes, statsRes] = await Promise.all([
        api.get('/finance/subscriptions', { params }),
        api.get('/finance/stats'),
      ]);
      setSubscriptions(subsRes.data.subscriptions || []);
      setStats(statsRes.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const filtered = subscriptions.filter((s) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q)
      || s.ownerId?.toLowerCase().includes(q)
      || s.ownerType?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading subscriptions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Subscriptions</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Premium and Alpha memberships across users and groups
          </p>
        </div>
      </div>

      {stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Active Premium</p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{stats.activePremium}</p>
            <p className="text-xs text-gray-400 mt-1">{stats.activePremiumUsers} users · {stats.activePremiumGroups} groups</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Alpha</p>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">{stats.alpha}</p>
            <p className="text-xs text-gray-400 mt-1">{stats.alphaUsers} users · {stats.alphaGroups} groups</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Expiring (7d)</p>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">{stats.expiringSoon}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Revenue (30d)</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">${(stats.revenue30d || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-400 mt-1">{stats.payments30d} payments</p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search name or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="alpha">Alpha</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Account</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Start</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expires</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden md:table-cell">Days left</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filtered.map((sub) => (
              <tr key={`${sub.ownerType}-${sub.ownerId}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900 dark:text-white">{sub.name || sub.ownerId}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{sub.ownerId}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 text-xs font-semibold rounded-full ${
                    sub.ownerType === 'user' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                  }`}>
                    {sub.ownerType}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap"><TierBadge tier={sub.tier} /></td>
                <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={sub.status} /></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{formatDate(sub.startDate)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  {sub.tier === 'alpha' ? 'Never' : formatDate(sub.expiryDate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-400 hidden md:table-cell">
                  {sub.status === 'alpha' ? '∞' : sub.daysRemaining != null ? (sub.daysRemaining > 0 ? sub.daysRemaining : '—') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 ? (
          <p className="p-6 text-center text-gray-500 dark:text-gray-400">No subscriptions found.</p>
        ) : null}
      </div>
    </div>
  );
}
